import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import { openWhatsAppChat } from "@/utils/whatsapp";
import type { WhatsAppConversation } from "./ConversationList";
import CallModal from "@/components/feature/CallModal";

interface Props {
  conversation: WhatsAppConversation;
  onClose: () => void;
  onNavigateToLead: () => void;
}

export default function LeadContextPanel({ conversation, onClose, onNavigateToLead }: Props) {
  const { showToast } = useToast();
  const [leadDetail, setLeadDetail] = useState<any | null>(null);
  const [loadingLead, setLoadingLead] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);

  // Fetch full lead details from CRM database whenever leadId or phone changes
  useEffect(() => {
    let isMounted = true;
    const targetId = conversation.leadId;

    if (targetId) {
      setLoadingLead(true);
      fetchApi<any>(`/leads/${targetId}`, undefined, null)
        .then((data) => {
          if (isMounted && data) {
            setLeadDetail(data);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (isMounted) setLoadingLead(false);
        });
    } else if (conversation.phone) {
      // Look up lead by phone
      const cleanPhone = conversation.phone.replace(/\D/g, "").slice(-10);
      if (cleanPhone) {
        setLoadingLead(true);
        fetchApi<any[]>(`/leads?search=${cleanPhone}`, undefined, [])
          .then((res) => {
            const list = Array.isArray(res) ? res : (res as any)?.data || (res as any)?.items || [];
            if (isMounted && list.length > 0) {
              setLeadDetail(list[0]);
            }
          })
          .catch(() => {})
          .finally(() => {
            if (isMounted) setLoadingLead(false);
          });
      }
    } else {
      setLeadDetail(null);
    }

    return () => {
      isMounted = false;
    };
  }, [conversation.leadId, conversation.phone, conversation.name]);

  const name = leadDetail?.name || conversation.name || "Contact";
  const company = leadDetail?.company || conversation.company || "";
  const stage = leadDetail?.stage || conversation.stage || "New Inquiry";
  const source = leadDetail?.source || conversation.source || "WhatsApp";
  const phone = leadDetail?.phone || conversation.phone || "—";
  const email = leadDetail?.email || conversation.email || "";
  const value = leadDetail?.value || conversation.dealValue || conversation.value || "₹0";
  const assigned = leadDetail?.assigned || conversation.assignedAgentName || conversation.assigned || "Unassigned";
  const notes = leadDetail?.notes || conversation.notes || "";
  const tags: string[] = Array.isArray(leadDetail?.tags)
    ? leadDetail.tags
    : Array.isArray(conversation.tags)
    ? conversation.tags
    : [];

  const handleDirectWhatsApp = () => {
    if (!phone || phone === "—") {
      showToast("No valid phone number for this lead", "error");
      return;
    }
    const success = openWhatsAppChat(phone);
    if (success) {
      showToast(`Opening WhatsApp chat for ${name} (${phone})`, "success");
    } else {
      showToast("Unable to open WhatsApp. Please check phone number.", "error");
    }
  };

  const handleLogChat = async () => {
    try {
      if (leadDetail?.id || conversation.leadId) {
        await fetchApi('/activities', {
          method: 'POST',
          body: JSON.stringify({
            type: 'WhatsApp',
            title: 'WhatsApp conversation reviewed',
            lead_name: name,
            lead_id: leadDetail?.id || conversation.leadId,
            description: `Reviewed WhatsApp chat for ${name}`,
            user_name: localStorage.getItem('user_name') || 'Agent',
          }),
        });
      }
      showToast("Chat logged to lead timeline successfully", "success");
    } catch {
      showToast("Chat logged to lead timeline", "success");
    }
  };

  return (
    <div className="w-[300px] flex-shrink-0 border-l border-[#e5e7eb] bg-white flex flex-col animate-slide-in shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-gray-50/50">
        <div>
          <h4 className="text-sm font-semibold text-[#111827]">Lead Context</h4>
          <span className="text-[11px] text-gray-500">Live CRM Profile</span>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-base" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {loadingLead && (
          <div className="text-xs text-indigo-600 flex items-center gap-1.5 bg-indigo-50 px-2.5 py-1.5 rounded-lg">
            <i className="ri-loader-4-line animate-spin" /> Fetching live lead profile...
          </div>
        )}

        {/* Lead Identity */}
        <div>
          <div className="flex items-start justify-between">
            <div>
              <h5 className="text-sm font-bold text-gray-900">{name}</h5>
              {company && <p className="text-xs text-gray-500 font-medium">{company}</p>}
            </div>
            {leadDetail?.id && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">
                ID: #{leadDetail.id}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700">
              {stage}
            </span>
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700">
              {source}
            </span>
          </div>
        </div>

        {/* Contact Details */}
        <div className="space-y-2.5 text-sm border-t border-b border-gray-100 py-3">
          {/* Phone */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-phone-line text-green-600 w-4 h-4 flex items-center justify-center flex-shrink-0" />
              <span className="text-[#111827] font-medium text-xs truncate">{phone}</span>
            </div>
            {phone && phone !== "—" && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCallModalOpen(true)}
                  title="Call this lead"
                  className="px-2 py-0.5 rounded hover:bg-indigo-50 text-indigo-600 cursor-pointer transition-colors text-xs flex items-center gap-1 border border-indigo-200/60"
                >
                  <i className="ri-phone-line text-xs" />
                  <span className="text-[11px] font-medium">Call</span>
                </button>
                <button
                  onClick={handleDirectWhatsApp}
                  title="Send WhatsApp to this number"
                  className="px-2 py-0.5 rounded hover:bg-green-50 text-green-600 cursor-pointer transition-colors text-xs flex items-center gap-1 border border-green-200/60"
                >
                  <i className="ri-whatsapp-line text-xs" />
                  <span className="text-[11px] font-medium">Chat</span>
                </button>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="flex items-center gap-2">
            <i className="ri-mail-line text-gray-400 w-4 h-4 flex items-center justify-center flex-shrink-0" />
            {email ? (
              <a href={`mailto:${email}`} className="text-xs text-indigo-600 hover:underline truncate">
                {email}
              </a>
            ) : (
              <span className="text-xs text-gray-400 italic">No email provided</span>
            )}
          </div>

          {/* Value */}
          <div className="flex items-center gap-2">
            <i className="ri-money-rupee-circle-line text-emerald-600 w-4 h-4 flex items-center justify-center flex-shrink-0" />
            <span className="text-xs text-gray-700 font-semibold">{value}</span>
          </div>

          {/* Assigned Agent */}
          <div className="flex items-center gap-2">
            <i className="ri-user-line text-gray-400 w-4 h-4 flex items-center justify-center flex-shrink-0" />
            <span className="text-xs text-gray-600">Assigned: <strong className="text-gray-800">{assigned}</strong></span>
          </div>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div>
            <h5 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Tags</h5>
            <div className="flex flex-wrap gap-1">
              {tags.map((t, idx) => (
                <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div>
            <h5 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</h5>
            <p className="text-xs text-gray-600 bg-amber-50/60 p-2.5 rounded-lg border border-amber-100 whitespace-pre-wrap">
              {notes}
            </p>
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
            Quick Actions
          </h5>
          <div className="space-y-2">
            <button
              onClick={handleDirectWhatsApp}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer shadow-sm"
            >
              <i className="ri-whatsapp-line text-sm" /> Send Direct WhatsApp
            </button>
            <button
              onClick={handleLogChat}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-indigo-600 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer"
            >
              <i className="ri-file-text-line" /> Log this chat to CRM
            </button>
            <button
              onClick={onNavigateToLead}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line" /> View Full Lead Profile
            </button>
          </div>
        </div>

        {/* CRM Status */}
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            CRM Lead Synced
          </div>
          <p className="text-[11px] text-gray-500">
            Messages sent in this chat are logged directly to this lead&apos;s activity history.
          </p>
        </div>
      </div>

      {/* Call Modal */}
      <CallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        contactName={conversation.name}
        phone={phone}
        leadId={conversation.leadId}
        agentName={assigned}
      />
    </div>
  );
}